import { Select as BaseSelect } from '@base-ui/react/select';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@core/utils/cn';

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SelectProps<T extends string> {
  value: T | null;
  onValueChange: (value: T) => void;
  options: readonly SelectOption<T>[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Rendered in the trigger. Defaults to the matching option's label. */
  children?: ReactNode;
}

/**
 * A single-choice select.
 *
 * <p>Base UI rather than a native `<select>`: the popup has to match the rest of the
 * design language, and the native control cannot be styled beyond its trigger on any
 * platform we care about. Positioning, keyboard handling and the focus trap come from the
 * library — the one thing not to do here is reimplement any of them.
 */
export function Select<T extends string>({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  id,
  disabled,
  invalid,
}: SelectProps<T>) {
  return (
    <BaseSelect.Root
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      disabled={disabled}
    >
      <BaseSelect.Trigger
        id={id}
        aria-invalid={invalid || undefined}
        className={cn(
          'bg-fill-subtle flex h-10 w-full items-center justify-between gap-2 rounded-md px-3',
          'text-fg ring-separator text-[15px] ring-1 ring-inset',
          'transition-[box-shadow,background-color] duration-150',
          'focus-visible:ring-accent outline-none focus-visible:ring-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          invalid && 'ring-danger focus-visible:ring-danger',
        )}
      >
        {/*
          A render function, not a bare <Select.Value />. Left to itself Base UI prints the
          selected VALUE, so a trigger shows "OWNER" or "__all__" where the list shows
          "Owner" and "Every tracker" -- the raw enum leaks into the interface and the two
          disagree. Looking the label up here keeps one source of truth for both.
        */}
        <BaseSelect.Value placeholder={<span className="text-fg-subtle">{placeholder}</span>}>
          {(current) =>
            options.find((option) => option.value === current)?.label ?? (
              <span className="text-fg-subtle">{placeholder}</span>
            )
          }
        </BaseSelect.Value>
        <BaseSelect.Icon className="text-fg-subtle">
          <ChevronsUpDown className="size-4" />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>

      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={6} className="z-50">
          <BaseSelect.Popup
            className={cn(
              // Sized from Base UI's own anchor variables rather than measured by hand,
              // which is most of the reason for adopting the library.
              'max-h-(--available-height) min-w-(--anchor-width) overflow-y-auto',
              'rounded-card bg-overlay shadow-e2 origin-(--transform-origin) p-1.5',
              'ring-separator ring-1 ring-inset',
              'ease-spring transition-[transform,opacity] duration-200',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              'motion-reduce:transition-none',
            )}
          >
            {options.map((option) => (
              <BaseSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  'grid cursor-default grid-cols-[1.25rem_1fr] items-center gap-2',
                  'rounded-md px-2.5 py-2 text-[14px] outline-none select-none',
                  'data-highlighted:bg-fill-subtle data-highlighted:text-fg',
                  'data-disabled:pointer-events-none data-disabled:opacity-40',
                )}
              >
                {/*
                  The indicator is wrapped in a cell that is ALWAYS rendered.
                  `Select.ItemIndicator` emits nothing at all when its item is not
                  selected, so on its own it is not a child of the grid -- and the label
                  then lands in the 1.25rem indicator column instead of the `1fr` one.
                  Every unselected option wrapped one word per line inside 20px, which on
                  a tracker picker turned "Truck 01 · Unit 01" into a five-line stack. It
                  is worst on a phone but it was never right anywhere; a selected item
                  hid it, because then the indicator does exist.
                */}
                <span className="flex size-5 items-center justify-center">
                  <BaseSelect.ItemIndicator>
                    <Check className="text-accent size-4" />
                  </BaseSelect.ItemIndicator>
                </span>
                <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
